import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Loader2, Mail, Phone, ChevronRight, AlertTriangle, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BulkClientImportDialog from '../../components/admin/BulkClientImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [desyncIds, setDesyncIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', password: '', phone: '' });
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try {
      const [resClients, resDesync] = await Promise.all([
        axios.get(`${API}/trainer/clients`),
        axios.get(`${API}/trainer/clients/desync-check`).catch(() => ({ data: { desync_client_ids: [] } })),
      ]);
      setClients(resClients.data);
      setDesyncIds(new Set(resDesync.data?.desync_client_ids || []));
    } catch (e) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newClient.name || !newClient.email || !newClient.password) {
      toast.error('Nombre, email y contrase\u00f1a son obligatorios');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/trainer/clients`, newClient);
      toast.success('Cliente creado');
      setShowCreate(false);
      setNewClient({ name: '', email: '', password: '', phone: '' });
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  return (
    <TrainerLayout>
      <div className="space-y-6" data-testid="trainer-clients">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Mis Clientes</h1>
            <p className="text-muted-foreground">{clients.length} clientes</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>

        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-import-clients-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Carga masiva (CSV)
          </Button>
        </div>

        <BulkClientImportDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCreated={fetchClients}
          role="trainer"
        />

        {desyncIds.size > 0 && (
          <div
            className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-md p-3 text-sm"
            data-testid="desync-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <strong className="text-red-300">{desyncIds.size} cliente{desyncIds.size !== 1 ? 's' : ''} desincronizado{desyncIds.size !== 1 ? 's' : ''}</strong>
              <p className="text-xs text-muted-foreground mt-0.5">
                El plan del macro activo no coincide con el plan asignado. Abre cada uno y usa <strong>"Posición en el Macro"</strong> para reposicionarlo.
              </p>
            </div>
          </div>
        )}

        {clients.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">A{'\u00fa'}n no tienes clientes</p>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" /> Crear primer cliente
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => {
              const isDesync = desyncIds.has(client.id);
              return (
              <Card key={client.id} className={`bg-card card-hover cursor-pointer ${isDesync ? 'border-red-500/50 ring-1 ring-red-500/20' : 'border-border'}`} onClick={() => navigate(`/trainer/clients/${client.id}`)} data-testid={`client-card-${client.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {client.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-foreground">{client.name}</p>
                        {isDesync && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-medium"
                            title="Posible desincronización del macro. Abre el cliente y reposiciónalo desde 'Posición en el Macro'."
                            data-testid={`desync-badge-${client.id}`}
                          >
                            <AlertTriangle className="h-3 w-3" /> Desincronizado
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{client.email}</span>
                        {client.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{client.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}

        {/* Create Client Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={newClient.name} onChange={(e) => setNewClient(p => ({...p, name: e.target.value}))} placeholder="Nombre completo" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newClient.email} onChange={(e) => setNewClient(p => ({...p, email: e.target.value}))} placeholder="correo@ejemplo.com" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Contrase{'\u00f1'}a *</Label>
                <Input type="password" value={newClient.password} onChange={(e) => setNewClient(p => ({...p, password: e.target.value}))} placeholder="Min 6 caracteres" className="bg-zinc-900/50 border-zinc-800" />
              </div>
              <div>
                <Label>Tel{'\u00e9'}fono</Label>
                <Input value={newClient.phone} onChange={(e) => setNewClient(p => ({...p, phone: e.target.value}))} placeholder="Opcional" className="bg-zinc-900/50 border-zinc-800" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TrainerLayout>
  );
};

export default TrainerClientsPage;
